import { api } from './api';

/**
 * Medición de uso propia (plan de enganche F0, 27-oct-2026).
 *
 * Registra vistas de pantalla y acciones clave en la BD de Wiservet
 * (POST /usage/events → tabla usage_events). SIN terceros (no PostHog) y SIN
 * datos personales: solo se manda la RUTA como patrón (ej. /app/pacientes/:id,
 * nunca el id real ni la query) o la clave de la acción (ej. bandeja.delegar).
 * El backend agrega usuario, clínica y rol desde el token, y marca la sesión
 * como interna (suplantación, superadmin, QA, automatizada, demo).
 *
 * Best-effort: nunca lanza ni bloquea la UI. Agrupa eventos y los envía cada
 * pocos segundos o al ocultar la pestaña.
 */

export type UsageEventType = 'page_view' | 'action';

interface UsageEvent {
  type: UsageEventType;
  name: string;
}

const FLUSH_DELAY_MS = 3000;
const MAX_BATCH = 10;

let queue: UsageEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let lastPageView = '';

/** Ruta como patrón: sin query, hash ni ids (el backend vuelve a normalizar). */
export function toRoutePattern(path: string): string {
  const clean = (path || '').split(/[?#]/)[0] || '/';
  return clean
    .split('/')
    .map((seg) => {
      if (!seg || seg.startsWith(':')) return seg;
      // uuid, números, RUT, folios: cualquier segmento con 4+ dígitos es un id.
      if ((seg.match(/\d/g) ?? []).length >= 4) return ':id';
      return /^[a-z0-9][a-z0-9_.-]*$/.test(seg) ? seg : ':x';
    })
    .join('/');
}

function isAutomated(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch {
    return false;
  }
}

export async function flushUsage(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const events = queue.slice(0, MAX_BATCH * 2);
  queue = queue.slice(events.length);
  try {
    await api.post('/usage/events', { events, webdriver: isAutomated() });
  } catch {
    // Medición best-effort: un fallo no debe molestar al usuario.
  }
}

function enqueue(ev: UsageEvent): void {
  queue.push(ev);
  if (queue.length >= MAX_BATCH) {
    void flushUsage();
    return;
  }
  if (!timer) timer = setTimeout(() => void flushUsage(), FLUSH_DELAY_MS);
}

/**
 * Vista de pantalla. `pattern` debe ser el patrón de la ruta (to.matched).
 * Ignora repeticiones consecutivas de la misma pantalla.
 */
export function trackPageView(pattern: string): void {
  const name = toRoutePattern(pattern);
  if (!name || name === lastPageView) return;
  lastPageView = name;
  enqueue({ type: 'page_view', name });
}

/** Acción clave (clave fija en código, ej. 'bandeja.delegar'). */
export function trackAction(name: string): void {
  if (!/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*){0,3}$/.test(name)) return;
  enqueue({ type: 'action', name });
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushUsage();
  });
}

/** Solo tests: reinicia el estado interno. */
export function __resetUsageForTests(): void {
  queue = [];
  lastPageView = '';
  if (timer) clearTimeout(timer);
  timer = null;
}
